import React, { useState, forwardRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Textarea.css';

/**
 * Textarea Component
 * Multi-line input with character count
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Textarea label
 * @param {string} props.value - Textarea value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.maxLength - Maximum character count
 * @param {number} props.rows - Number of rows
 * @param {boolean} props.showCount - Show character count
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field
 * @param {boolean} props.resizable - Allow resize
 */
const Textarea = forwardRef(
  (
    {
      label,
      value = '',
      onChange,
      placeholder = '',
      maxLength,
      rows = 4,
      showCount = true,
      error,
      disabled = false,
      required = false,
      resizable = true,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const currentLength = value?.length || 0;

    const containerClasses = `
      textarea-container
      ${error ? 'textarea-container--error' : ''}
      ${disabled ? 'textarea-container--disabled' : ''}
      ${isFocused ? 'textarea-container--focused' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    const textareaClasses = `
      textarea-field
      ${!resizable ? 'textarea-field--no-resize' : ''}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={textareaId} className="textarea-label">
            {label}
            {required && <span className="textarea-required">*</span>}
          </label>
        )}

        <motion.textarea
          ref={ref}
          id={textareaId}
          name={name}
          className={textareaClasses}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          required={required}
          rows={rows}
          maxLength={maxLength}
          aria-label={label || placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? `${textareaId}-error` : undefined}
          whileFocus={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
          {...props}
        />

        {(showCount || error) && (
          <div className="textarea-footer">
            {error && (
              <motion.span
                id={`${textareaId}-error`}
                className="textarea-error"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                role="alert"
              >
                {error}
              </motion.span>
            )}

            {showCount && maxLength && (
              <span
                className={`textarea-count ${
                  currentLength >= maxLength ? 'textarea-count--max' : ''
                }`}
              >
                {currentLength} / {maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  rows: PropTypes.number,
  showCount: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  resizable: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Textarea;
